import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { useLenis } from './SmoothScroll';

gsap.registerPlugin(useGSAP);

/**
 * First-load curtain: the name snap-cuts through eight display faces,
 * every frame sharing one optical size and one center anchor, then
 * settles on the brand face (Le Murmure) and the panel lifts.
 * Hero intro starts at INTRO_DELAY (see Hero.tsx).
 */
const FRAMES: { key: string; cls: string; text?: string }[] = [
  { key: 'sans', cls: 'lk-sans' },
  { key: 'aalto', cls: 'lk-aalto' },
  { key: 'nighty', cls: 'lk-nighty' },
  { key: 'skyscapers', cls: 'lk-skyscapers' },
  { key: 'comico', cls: 'lk-comico' },
  { key: 'struggle', cls: 'lk-struggle' },
  { key: 'shadowhand', cls: 'lk-shadowhand' },
  { key: 'murmure', cls: 'lk-murmure' },
];

const FRAME_MS = 165;

const Preloader: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [done, setDone] = useState(false);
  const [frame, setFrame] = useState(0);
  const { setLocked } = useLenis();

  // Flipbook: advance one face per beat, hold the last
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const interval = window.setInterval(() => {
      setFrame(f => {
        if (f >= FRAMES.length - 1) {
          clearInterval(interval);
          return f;
        }
        return f + 1;
      });
    }, FRAME_MS);
    return () => clearInterval(interval);
  }, []);

  useGSAP(
    () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setDone(true);
        return;
      }
      setLocked(true);
      window.scrollTo(0, 0);

      gsap
        .timeline({
          onComplete: () => {
            setLocked(false);
            setDone(true);
          },
        })
        .to(ref.current, { yPercent: -100, duration: 0.85, ease: 'power4.inOut' }, 1.55);
    },
    { scope: ref }
  );

  if (done) return null;

  return (
    <div
      ref={ref}
      aria-hidden='true'
      className='fixed inset-0 z-[100] bg-paper text-ink flex flex-col will-change-transform'
    >
      <div className='flex-1 flex items-center justify-center px-6'>
        <div className='logo-swap logo-swap--center text-[clamp(2.4rem,8vw,5.5rem)]'>
          {FRAMES.map((f, i) => (
            <span
              key={f.key}
              style={{ opacity: i === frame ? 1 : 0 }}
              className='inline-flex items-center justify-center'
            >
              <span className={`lk ${f.cls}`}>
                Akash&nbsp;Roy
                {f.key === 'murmure' && <span className='text-accent'>.</span>}
              </span>
            </span>
          ))}
        </div>
      </div>

      <div className='px-6 sm:px-10 pb-8 sm:pb-10 flex items-end justify-between label-mono text-gray1'>
        <span>Portfolio 2026</span>
        <span className='tabular-nums'>
          {String(frame + 1).padStart(2, '0')}
          <span className='text-accent'>/</span>
          {String(FRAMES.length).padStart(2, '0')}
        </span>
      </div>
    </div>
  );
};

export default Preloader;
